import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, FormikProvider, Form } from 'formik';
import { useSnackbar } from 'notistack';
import {
  Box,
  Stack,
  Typography,
  Avatar,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import dayjs from 'dayjs';
import {
  addComment,
  getComments,
  updateComment,
  deleteComment,
  clearTaskComments,
} from '../../../redux/features/taskManagementSlice';
import GlobalAllTypeFileUpload from '../../../components/Fileupload/GlobalAllTypeFileUpload';
import GlobalAllTypeFileView from '../../../components/Fileupload/GlobalAllTypeFileView';

// ---- helpers ----
const AV_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#3B82F6', '#F43F5E'];
const avatarColor = (name = '') =>
  AV_COLORS[Math.abs((name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % AV_COLORS.length];

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('cred'))?.profile || {};
  } catch {
    return {};
  }
};

const fmt = (t) => (t && dayjs(t).isValid() ? dayjs(t).format('DD MMM YYYY, hh:mm A') : '');

// Build the public URL for an uploaded attachment key (same scheme as GlobalAllTypeFileView).
const attachmentUrl = (name) => `${process.env.REACT_APP_CONTABO_UPLOADS_BASE_URL}/${name}`;

// Force-download an attachment. The uploads bucket is cross-origin, so an <a download>
// alone is ignored by browsers — we fetch the blob to trigger a real download, and fall
// back to opening in a new tab if the fetch is blocked (e.g. CORS).
const downloadAttachment = async (name) => {
  const url = attachmentUrl(name);
  const fileName = (name?.split('/').pop() || 'attachment').split('?')[0];
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('download failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return false;
  }
};

// Normalize an unknown-shaped comment object into a predictable one.
// `links` may come back as an array of objects ({ url, type, label }) or of
// plain key strings; we flatten to an array of key strings for view/re-send.
const normalizeComment = (c = {}) => {
  const rawLinks = c.links ?? c.attachments ?? c.comment_links ?? [];
  const links = Array.isArray(rawLinks)
    ? rawLinks
        .map((l) => (typeof l === 'string' ? l : l?.url ?? l?.file_key ?? l?.link ?? l?.path))
        .filter(Boolean)
    : typeof rawLinks === 'string' && rawLinks.trim()
      ? rawLinks.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
  return {
    id: c.comment_id ?? c.id,
    text: c.comment_text ?? c.comment ?? c.text ?? '',
    authorName: c.created_by_name ?? c.commented_by_name ?? c.user_name ?? c.name ?? 'User',
    authorId: c.created_by ?? c.commented_by ?? c.user_id ?? c.userId,
    createdAt: c.created_at ?? c.commented_at ?? c.createdAt ?? c.updated_at,
    edited: Boolean(
      c.is_edited ?? c.edited ?? (c.updated_at && c.created_at && c.updated_at !== c.created_at)
    ),
    links,
  };
};

const TaskComments = ({ taskId }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useMemo(getCurrentUser, []);

  const { taskComments } = useSelector((state) => state.taskManagement);

  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  // taskId whose comments are currently in the store; guards against showing the
  // previous task's comments before the new fetch resolves.
  const [loadedFor, setLoadedFor] = useState(null);

  const comments = useMemo(
    () => (Array.isArray(taskComments) ? taskComments.map(normalizeComment) : []),
    [taskComments]
  );

  const load = () => {
    if (!taskId) return;
    setFetching(true);
    dispatch(getComments(taskId)).finally(() => {
      setFetching(false);
      setLoadedFor(taskId);
    });
  };

  const ready = loadedFor === taskId;

  useEffect(() => {
    dispatch(clearTaskComments()); // drop previous task's comments immediately
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const isOwner = (c) =>
    (currentUser?.userId != null && String(c.authorId) === String(currentUser.userId)) ||
    (currentUser?.name && c.authorName && currentUser.name === c.authorName);

  // ---- composer ----
  const formik = useFormik({
    initialValues: { comment_text: '', links: [] },
    onSubmit: (values, { resetForm }) => {
      const text = values.comment_text.trim();
      if (!text && !values.links.length) {
        enqueueSnackbar('Write a comment or attach a file', { variant: 'warning' });
        return;
      }
      setSubmitting(true);
      dispatch(addComment({ task_id: taskId, comment_text: text, links: values.links })).then((res) => {
        setSubmitting(false);
        if (res?.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message || 'Comment added', { variant: 'success' });
          resetForm();
          load();
        } else {
          enqueueSnackbar(res?.payload?.message || 'Failed to add comment', { variant: 'error' });
        }
      });
    },
  });

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text);
  };

  const saveEdit = (c) => {
    const text = editText.trim();
    if (!text) {
      enqueueSnackbar('Comment cannot be empty', { variant: 'warning' });
      return;
    }
    dispatch(updateComment({ comment_id: c.id, comment_text: text, links: c.links })).then((res) => {
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Comment updated', { variant: 'success' });
        setEditingId(null);
        setEditText('');
        load();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to update comment', { variant: 'error' });
      }
    });
  };

  const handleDownload = async (c) => {
    if (!c.links?.length) return;
    setDownloadingId(c.id);
    let anyFallback = false;
    for (const name of c.links) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await downloadAttachment(name);
      if (!ok) anyFallback = true;
    }
    setDownloadingId(null);
    if (anyFallback) {
      enqueueSnackbar('Some files opened in a new tab (direct download blocked)', { variant: 'info' });
    }
  };

  const handleDelete = (c) => {
    setDeletingId(c.id);
    dispatch(deleteComment(c.id)).then((res) => {
      setDeletingId(null);
      if (res?.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message || 'Comment deleted', { variant: 'success' });
        load();
      } else {
        enqueueSnackbar(res?.payload?.message || 'Failed to delete comment', { variant: 'error' });
      }
    });
  };

  return (
    <Box>
      {/* Comment list */}
      <Box
        sx={{
          maxHeight: 320,
          overflowY: 'auto',
          pr: 0.5,
          mb: 2,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { background: '#E2E8F0', borderRadius: 3 },
        }}
      >
        {!ready ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
            <CircularProgress size={26} />
          </Stack>
        ) : comments.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 5, color: '#94A3B8' }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: '#CBD5E1' }} />
            <Typography sx={{ fontSize: '0.85rem' }}>No comments yet. Start the conversation.</Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {comments.map((c) => {
              const mine = isOwner(c);
              const editing = editingId === c.id;
              return (
                <Stack key={c.id ?? c.createdAt} direction="row" spacing={1.25} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      bgcolor: `${avatarColor(c.authorName)}22`,
                      color: avatarColor(c.authorName),
                      mt: 0.25,
                    }}
                  >
                    {c.authorName?.charAt(0)?.toUpperCase() || '?'}
                  </Avatar>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      background: mine
                        ? 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)'
                        : '#F8FAFC',
                      border: '1px solid',
                      borderColor: mine ? '#E0E7FF' : '#EEF2F6',
                      borderRadius: 2.5,
                      px: 1.75,
                      py: 1.25,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E293B' }} noWrap>
                          {c.authorName}
                          {mine && <span style={{ color: '#6366F1', fontWeight: 600 }}> (You)</span>}
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }} noWrap>
                          {fmt(c.createdAt)}
                          {c.edited ? ' · edited' : ''}
                        </Typography>
                      </Stack>
                      {mine && !editing && (
                        <Stack direction="row" spacing={0.25}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => startEdit(c)} sx={{ color: '#64748B' }}>
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              disabled={deletingId === c.id}
                              onClick={() => handleDelete(c)}
                              sx={{ color: '#EF4444' }}
                            >
                              {deletingId === c.id ? (
                                <CircularProgress size={13} color="error" />
                              ) : (
                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>

                    {editing ? (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          fullWidth
                          multiline
                          size="small"
                          autoFocus
                        />
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                            onClick={() => {
                              setEditingId(null);
                              setEditText('');
                            }}
                            sx={{ textTransform: 'none', color: '#64748B' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckIcon sx={{ fontSize: 15 }} />}
                            onClick={() => saveEdit(c)}
                            sx={{
                              textTransform: 'none',
                              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                            }}
                          >
                            Save
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <>
                        {c.text && (
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize: '0.85rem',
                              color: '#334155',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            {c.text}
                          </Typography>
                        )}
                        {c.links.length > 0 && (
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
                            <GlobalAllTypeFileView
                              attachmentFiles={c.links}
                              previewButtonName={`${c.links.length} attachment${c.links.length > 1 ? 's' : ''}`}
                            />
                            <Tooltip title={`Download attachment${c.links.length > 1 ? 's' : ''}`}>
                              <span>
                                <Button
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  disabled={downloadingId === c.id}
                                  onClick={() => handleDownload(c)}
                                  startIcon={
                                    downloadingId === c.id ? (
                                      <CircularProgress size={13} color="inherit" />
                                    ) : (
                                      <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                                    )
                                  }
                                  sx={{ textTransform: 'none' }}
                                >
                                  Download
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        )}
                      </>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Composer */}
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: 3,
              p: 1.5,
              background: '#fff',
            }}
          >
            <TextField
              name="comment_text"
              placeholder="Write a comment..."
              value={formik.values.comment_text}
              onChange={formik.handleChange}
              fullWidth
              multiline
              maxRows={4}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              sx={{ px: 0.5 }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <GlobalAllTypeFileUpload
                  accept="image/*,video/*,audio/*,application/pdf"
                  formik={formik}
                  dbFolderName="task"
                  label="Attach"
                  updateLabel="Attached"
                  previewButtonName="Preview"
                  formikNameArray="links"
                  multiple
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                endIcon={submitting ? <CircularProgress size={15} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  whiteSpace: 'nowrap',
                  background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                  '&:hover': { background: 'linear-gradient(135deg, #5558E6, #7C4FE0)' },
                }}
              >
                {submitting ? 'Sending' : 'Send'}
              </Button>
            </Stack>
          </Box>
        </Form>
      </FormikProvider>
    </Box>
  );
};

export default TaskComments;
